import { DestroyRef, Directive, input, OnInit, output } from '@angular/core';
import { FormControl, NgControl } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, distinctUntilChanged, Observable } from 'rxjs';

@Directive({
    selector: '[aiLoginUnico]',
    standalone: true,
})
export class LoginUnicoDirective implements OnInit {
    public loginUnicoFinder =
        input.required<(email: string) => Observable<any>>();
    public loginUnicoNameControl = input<FormControl>();
    public loginUnicoCpfControl = input<FormControl>();
    public loginUnicoEmailFound = output<any>();
    public loginUnicoError = output<any>();

    constructor(
        private readonly destroyRef: DestroyRef,
        private readonly controlDir: NgControl,
    ) {}

    ngOnInit(): void {
        this.getLoginUnico();
    }

    /**
     * Preenche os campos de nome e CPF com os dados
     * retornados pelo serviço de Login Único.
     *
     */
    private fillControls(data: any): void {
        if (!data) {
            this.loginUnicoError.emit({ notFound: true });
            return;
        }

        this.loginUnicoNameControl()?.setValue(data.name);
        this.loginUnicoCpfControl()?.setValue(data.cpf);
    }

    /**
     * Obtém do serviço os dados da empresa, caso o CNPJ informado
     * no campo tenha formação válida.
     */
    private getLoginUnico(): void {
        const control = this.controlDir.control;

        if (!control) {
            return;
        }

        control.valueChanges
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                distinctUntilChanged(),
                debounceTime(1000),
            )
            .subscribe((value: string) => {
                if (value) {
                    const newEmail = value.replace(/\s/g, '').toLowerCase();
                    if (value !== newEmail) {
                        control.setValue(newEmail, { emitEvent: false });
                    }
                    if (newEmail.isEmail()) {
                        this.loginUnicoFinder()(newEmail)
                            .pipe(takeUntilDestroyed(this.destroyRef))
                            .subscribe({
                                next: (data: any) => {
                                    this.fillControls(data);
                                    this.loginUnicoEmailFound.emit(data);
                                },
                                error: (error: any) => {
                                    this.handleError(error.status);
                                },
                            });
                    }
                } else {
                    this.loginUnicoError.emit({ invalid: true });
                    this.loginUnicoNameControl()?.setValue('');
                    this.loginUnicoCpfControl()?.setValue('');
                }
            });
    }

    /**
     * Lida com erros ao buscar dados do Email.
     * Atualiza o campo de nome e CPF.
     *
     * @param errorStatus Objeto de erro retornado pelo serviço.
     */
    private handleError(errorStatus: number): void {
        this.loginUnicoNameControl()?.setValue('');
        this.loginUnicoCpfControl()?.setValue('');

        if (errorStatus === 404 || errorStatus === 422) {
            this.loginUnicoError.emit({ notFound: true });
        } else if (errorStatus === 400) {
            this.loginUnicoError.emit({ invalid: true });
        } else {
            this.loginUnicoError.emit({ serverError: true });
        }
    }
}
